"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
export default function RequestQuote() {
  return <section data-section-id="221558" id="custom-solution-request" className="relative py-24 md:py-32 bg-[radial-gradient(ellipse_at_center,var(--color-muted)_0%,var(--color-background)_70%)] overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl relative z-10">
        <div className="text-center mb-16">
          <span className="block font-mono text-sm tracking-[0.2em] text-primary uppercase mb-4">
            Custom Quotes
          </span>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-secondary font-bold tracking-tight">
            Get a Professional Sanitation Quote
          </h2>
        </div>

        <FormProvider formKey="custom_solution_request_home" formId="custom_solution_request_home" sectionName="custom-solution-request" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        event_type: "",
        event_date: "",
        flower_quantity: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        event_type: {
          required: true
        },
        event_date: {
          required: true
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Request Quote" successMessage="Thank you for your request. Our logistics team will review your requirements and provide a custom quote within 24 hours.">
          <QuoteForm />
        </FormProvider>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const underlinedInputClass = "border-0 border-b border-primary/20 rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary bg-transparent h-12 text-lg font-default transition-colors placeholder:text-muted-foreground/40 w-full";
  if (isSubmitted && !submitError) {
    return <div className="p-8 md:p-12 bg-background/80 backdrop-blur-sm border border-primary/10 rounded-2xl text-center animate-in fade-in duration-500 shadow-xl">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-6">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-default text-2xl text-secondary font-bold mb-4">
          Request Received
        </h3>
        <p className="text-muted-foreground text-lg font-default mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg px-8 py-6 border-primary/20 text-primary hover:bg-primary/5 font-default">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8 md:space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        <FormField control={control} name="full_name" rules={{
        required: "Full name and company are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary/70 font-mono text-xs uppercase tracking-wider">
                Full Name & Company
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe | Enterprise Inc." className={underlinedInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Business email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid business email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary/70 font-mono text-xs uppercase tracking-wider">
                Business Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@company.com" className={underlinedInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary/70 font-mono text-xs uppercase tracking-wider">
                Contact Phone
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className={underlinedInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs mt-1 font-default">
                Optional for rapid coordination
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="event_type" rules={{
        required: "Please select a project category"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary/70 font-mono text-xs uppercase tracking-wider">
                Project Category
              </FormLabel>
              <FormControl>
                <select className={`${underlinedInputClass} appearance-none cursor-pointer`} {...field}>
                  <option value="" disabled>
                    Select project type
                  </option>
                  <option value="Construction">Construction & Industrial</option>
                  <option value="Special Event">Special Events & Weddings</option>
                  <option value="Municipal">Municipal & Government</option>
                  <option value="Emergency">Emergency Response</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        <FormField control={control} name="event_date" rules={{
        required: "Estimated start date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary/70 font-mono text-xs uppercase tracking-wider">
                Estimated Start Date
              </FormLabel>
              <FormControl>
                <Input type="date" className={`${underlinedInputClass} cursor-pointer`} {...field} />
              </FormControl>
              <FormDescription className="text-xs mt-1 font-default">
                When do you need the units delivered?
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="flower_quantity" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary/70 font-mono text-xs uppercase tracking-wider">
                Estimated Unit Quantity
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g. 10" className={underlinedInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs mt-1 font-default">
                Approximate number of units required
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide project location and specific details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary/70 font-mono text-xs uppercase tracking-wider">
              Project Location & Specific Requirements
            </FormLabel>
            <FormControl>
              <textarea placeholder="Enter project address and any specific sanitation needs (ADA, luxury trailers, handwash stations, etc.)" className={`${underlinedInputClass} min-h-[120px] resize-y py-3 font-default`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-center">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="pt-8 text-center">
        <Button type="submit" disabled={isSubmitting} className="rounded-lg bg-primary text-primary-foreground hover:scale-105 transition-transform duration-300 px-10 py-7 text-lg font-default font-bold shadow-lg hover:shadow-primary/20 w-full md:w-auto min-w-[240px]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 h-5 w-5" />
              Processing...
            </> : "Request Quote"}
        </Button>
      </div>
    </Form>;
}